import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { orderBy } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';

import {
  SIMILAR_PARAGRAPH_DEAL_COUNT_LIMIT,
  SIMILAR_PARAGRAPH_EXPAND_LIMIT,
  SIMILAR_PARAGRAPH_MATCHES_PER_DEAL,
} from '../../features/deal/constants/limits';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { SimilarParagraphService } from '../../features/similar-paragraph/services/similar-paragraph.service';
import { AggregatedSelectors } from '../aggregated/aggregated.selector';
import { DealActions } from '../deal/deal.actions';
import { RootState } from '../root-state.model';
import { SimilarParagraphActions } from './similar-paragraph.actions';
import { SimilarParagraphsSelectors } from './similar-paragraph.selectors';
import { TransactionSelectors } from '../transaction/transaction.selectors';

@Injectable()
export class SimilarParagraphsEffects {
  private service = inject(SimilarParagraphService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);

  search$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SimilarParagraphActions.SearchSimilarParagraphs),
      withLatestFrom(
        this.store,
        this.store.select(AggregatedSelectors.scopedTransactions),
        this.store.select(TransactionSelectors.listLookup)
      ),
      switchMap(([action, store, transactions, listLookup]) => {
        const searchText = action.searchText || store.similarParagraph.originalParagraph;
        let transactionList = orderBy(
          transactions || [],
          (t) => t.transactionDisplayClosingDate || false,
          'desc'
        ).filter((t) => t.transactionId !== store.deal.transaction.transactionId);

        transactionList = action.scanWithoutLimit
          ? transactionList
          : transactionList.slice(0, SIMILAR_PARAGRAPH_DEAL_COUNT_LIMIT);

        return this.service
          .getSimilars(
            searchText,
            transactionList.map((t) => t.transactionId),
            store.similarParagraph.groupByDeal ? SIMILAR_PARAGRAPH_MATCHES_PER_DEAL : undefined
          )
          .pipe(
            map((list) => {
              return this.service.createViewModels(list, listLookup);
            }),
            switchMap((viewModels) => this.service.makeDiff(viewModels, searchText)),
            map((list) =>
              SimilarParagraphActions.SearchSimilarParagraphsSuccess({ list, searchedDealCount: transactionList.length })
            ),
            reportEffectError(SimilarParagraphActions.SearchSimilarParagraphsFail)
          );
      })
    )
  );

  regroup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SimilarParagraphActions.SetGroupByDeal),
      withLatestFrom(
        this.store.select(SimilarParagraphsSelectors.originalParagraph),
        this.store.select(SimilarParagraphsSelectors.scanWithoutLimit)
      ),
      filter(([, originalParagraph]) => !!originalParagraph),
      map(([, , scanWithoutLimit]) => SimilarParagraphActions.SearchSimilarParagraphs({ scanWithoutLimit }))
    )
  );

  expandDeal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SimilarParagraphActions.ExpandDeal),
      withLatestFrom(
        this.store.select(SimilarParagraphsSelectors.originalParagraph),
        this.store.select(TransactionSelectors.listLookup)
      ),
      mergeMap(([{ transactionId }, searchText, listLookup]) =>
        this.service.getSimilars(searchText, [transactionId], SIMILAR_PARAGRAPH_EXPAND_LIMIT).pipe(
          map((paragraphSet) => this.service.createViewModels(paragraphSet, listLookup)),
          switchMap((viewModels) => this.service.makeDiff(viewModels, searchText)),
          map((list) => SimilarParagraphActions.ExpandDealSuccess({ transactionId, list })),
          catchError((error: unknown) => {
            console.error(error);
            return of(SimilarParagraphActions.ExpandDealFail({ transactionId, error: String(error) }));
          }),
          takeUntil(
            this.actions$.pipe(ofType(SimilarParagraphActions.SearchSimilarParagraphs, DealActions.LoadDealSuccess))
          )
        )
      )
    )
  );
}
